'use client';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { cryptoFinanceApi, getCryptoFinanceSidebar } from '@/services/api/cryptoFinance.api';
import { type EventResponse, type MarketsQueryParams, type PaginatedResponse } from '@/types';
import { type CategoryType } from '@/types/category.types';

export const useCryptoFinanceSidebar = (category: CategoryType) => {
  return useQuery({
    queryKey: ['crypto-finance-sidebar', category],
    queryFn: () => getCryptoFinanceSidebar(category),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Fetch crypto events (Single-fetch)
// Uses the dedicated /markets/events/crypto endpoint
export const useCryptoEvents = (params?: MarketsQueryParams) => {
  return useQuery({
    queryKey: ['crypto-events', params || 'all'],
    queryFn: () => cryptoFinanceApi.getCryptoEvents(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Fetch infinite crypto markets (for load more/infinite scroll)
// Specifically targets the crypto-only events endpoint with pagination support
export const useCryptoMarketsInfinite = (params?: MarketsQueryParams, options?: any) => {
  const initialOffset = params?.offset || 0;
  return useInfiniteQuery<PaginatedResponse<EventResponse>>({
    queryKey: ['crypto-events', params || {}, 'infinite'],
    queryFn: async ({ pageParam = initialOffset }) => {
      return cryptoFinanceApi.getCryptoEvents({ ...params, offset: pageParam as number });
    },
    initialPageParam: initialOffset,
    getNextPageParam: (lastPage) => {
      if (lastPage.hasNextPage) {
        return lastPage.page * lastPage.limit;
      }
      return undefined;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};
